import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { UseFormReturn, useForm, useWatch } from "react-hook-form";

import Skeleton from "../components/common/skeleton.comp";
import PosLayout from "../components/layouts/posLayout.comp";
import { CategoryBtns } from "../components/reuseables/categoryBtns.comp";
import PageNav from "../components/reuseables/pageNav.comp";
import { useTranslation } from "../core/translation.core";
import useCart from "../hooks/services/useCart.hook";
import useProductQuery from "../hooks/services/useProductQuery";
import ErrorCenterIcon from "../icons/exclamationTriangleCenter.icon";
import { Product, ProductListFilter } from "../types/product";

export default function PosProductsPage() {
  const { query, isReady } = useRouter();
  const [showOverlay, setShowOverlay] = useState(false);
  const methods = useForm<ProductListFilter>({
    defaultValues: {
      page: 1,
      pageSize: 20,
    },
  });

  // sync the page param back to form hook -
  useEffect(() => {
    methods.setValue("page", Number(query.page));
    methods.setValue("search", query.search as string);

    // eslint-disable-next-line
  }, [query, isReady]);

  return (
    <PosLayout title="Products">
      <section className="space-y-10">
        <CategoryBtns route="/products" showOverlay={showOverlay} setShowOverlay={setShowOverlay} />

        {!showOverlay && (
          <>
            <PosProductCard methods={methods} />
            <Paging methods={methods} />
          </>
        )}
      </section>
    </PosLayout>
  );
}

function PosProductCard({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const { msg, t } = useTranslation();
  const { data: products, isFetched, isError, failureReason } = useProductQuery(methods);

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (!isFetched) return <Skeleton />;
  if (!products?.list?.length) return <h1 className="dark:text-white text-2xl">{t(msg.product.noProductsFound)}</h1>;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4" id="pos-product-list">
      {products?.list?.map((p) => (
        <ProductCard key={p.id} product={p} />
      ))}
    </div>
  );
}

export function ProductCard({ product }: { product: Product }) {
  const { push } = useRouter();
  const { msg, t } = useTranslation();
  const { getProductWarehouseId } = useCart();

  return (
    <section
      attr-name={product?.name}
      id="pos-product"
      className="cursor-pointer space-y-4 rounded-lg bg-white dark:bg-her-darkblue2 dark:shadow-md dark:text-white shadow-sm text-center py-5"
      onClick={() => push(`/product/${product.id}`)}
    >
      <h1 className="font-bold text-xs">{product?.name}</h1>
      <h3 className="text-xs">SKU - {product.sku}</h3>
      <h4 className="font-bold text-base text-her-green">${product?.price}</h4>
      <p className="text-xs mt-1">
        {t(msg.product.inStock)}:
        {product.retailInventories?.find((inventory) => inventory.warehouseId === getProductWarehouseId(product))?.count || 0}
      </p>
    </section>
  );
}

function Paging({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const router = useRouter();
  const [pageSize] = useWatch({
    control: methods.control,
    name: ["pageSize"],
  });
  const { data: products } = useProductQuery(methods);

  const total = products?.size ?? 0;

  return <PageNav page={Number(router.query.page) || 1} size={pageSize} total={total} pathname="/products" />;
}
